import { useEffect, useState } from "react";
import { getProfiles, updateProfile, refreshProfiles } from "./StockProfileService";

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatDate = (str) => {
  if (!str) return "";
  try {
    return new Date(str).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return str;
  }
};

const emptyRow = (key) => ({ [key]: "", percent: 0 });

export default function StockProfile() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [editingSymbol, setEditingSymbol] = useState(null);
  const [editForm, setEditForm] = useState({ countries: [], segments: [], summary: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getProfiles();
      setProfiles(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load stock exposure data.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError("");
    try {
      const data = await refreshProfiles();
      if (Array.isArray(data) && data.length) {
        setProfiles(data);
      } else {
        await load();
      }
    } catch {
      setError("Failed to refresh stock exposure data.");
    } finally {
      setRefreshing(false);
    }
  };

  const startEdit = (profile) => {
    setEditingSymbol(profile.symbol);
    setEditForm({
      countries: (profile.countries || []).map((c) => ({ country: c.country, percent: c.percent })),
      segments: (profile.segments || []).map((s) => ({ segment: s.segment, percent: s.percent })),
      summary: profile.summary || "",
    });
  };

  const cancelEdit = () => {
    setEditingSymbol(null);
    setEditForm({ countries: [], segments: [], summary: "" });
  };

  const updateRow = (type, idx, field, value) => {
    setEditForm((prev) => {
      const rows = [...prev[type]];
      rows[idx] = { ...rows[idx], [field]: value };
      return { ...prev, [type]: rows };
    });
  };

  const addRow = (type) => {
    setEditForm((prev) => ({
      ...prev,
      [type]: [...prev[type], emptyRow(type === "countries" ? "country" : "segment")],
    }));
  };

  const removeRow = (type, idx) => {
    setEditForm((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== idx),
    }));
  };

  const saveEdit = async (profile) => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        symbol: profile.symbol,
        stockName: profile.stockName,
        countries: editForm.countries
          .filter((c) => c.country && c.country.trim())
          .map((c) => ({ country: c.country.trim(), percent: toNumber(c.percent) })),
        segments: editForm.segments
          .filter((s) => s.segment && s.segment.trim())
          .map((s) => ({ segment: s.segment.trim(), percent: toNumber(s.percent) })),
        summary: editForm.summary,
      };
      const updated = await updateProfile(profile.symbol, payload);
      if (updated) {
        setProfiles((prev) => prev.map((p) => (p.symbol === profile.symbol ? updated : p)));
        cancelEdit();
      } else {
        setError("Failed to save changes.");
      }
    } catch {
      setError("Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stocks-section">
      <div className="stocks-toolbar">
        <div className="card-title" style={{ marginBottom: 0 }}>Stock Exposure</div>
        <button className="btn btn-secondary" onClick={handleRefresh} disabled={refreshing || loading}>
          {refreshing ? "Refreshing…" : "↻ Refresh All"}
        </button>
      </div>

      {error && <div className="stocks-error">{error}</div>}

      {loading && <div className="stocks-empty">Loading stock exposure data…</div>}

      {!loading && profiles.length === 0 && (
        <div className="card">
          <div className="stocks-empty">
            No stocks to analyze yet. Add trades or watchlist items to see country and segment exposure here.
          </div>
        </div>
      )}

      {!loading && profiles.map((profile) => {
        const isEditing = editingSymbol === profile.symbol;
        const countries = profile.countries || [];
        const segments = profile.segments || [];
        const hasData = !!profile.updatedAt && (countries.length > 0 || segments.length > 0);

        return (
          <div className="card" key={profile.symbol}>
            <div className="stocks-tax-header">
              <div className="card-title" style={{ marginBottom: 0 }}>
                {profile.symbol} {profile.stockName ? `· ${profile.stockName}` : ""}
              </div>
              <div className="stocks-tax-summary">
                {profile.updatedAt && (
                  <span className="stocks-badge">
                    {profile.manualOverride ? "Manually edited" : "AI estimate"} · {formatDate(profile.updatedAt)}
                  </span>
                )}
                {!isEditing && (
                  <button className="btn btn-secondary" onClick={() => startEdit(profile)}>
                    Edit
                  </button>
                )}
              </div>
            </div>

            {!hasData && !isEditing && (
              <div className="stocks-empty">Not analyzed yet — click "Refresh All" to generate, or "Edit" to enter manually.</div>
            )}

            {!isEditing && profile.summary && (
              <p className="stocks-exposure-summary">{profile.summary}</p>
            )}

            {!isEditing && countries.length > 0 && (
              <div className="stocks-exposure-group">
                <div className="stocks-exposure-group-title">Geographic Revenue Exposure</div>
                {countries.map((c, idx) => (
                  <div className="stocks-exposure-row" key={`${c.country}-${idx}`}>
                    <div className="stocks-alloc-label">
                      <div className="stocks-alloc-name">{c.country}</div>
                      <div className="stocks-alloc-meta">{toNumber(c.percent).toFixed(1)}%</div>
                    </div>
                    <div className="stocks-alloc-bar">
                      <span style={{ width: `${Math.min(toNumber(c.percent), 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isEditing && segments.length > 0 && (
              <div className="stocks-exposure-group">
                <div className="stocks-exposure-group-title">Business Segment Exposure</div>
                {segments.map((s, idx) => (
                  <div className="stocks-exposure-row" key={`${s.segment}-${idx}`}>
                    <div className="stocks-alloc-label">
                      <div className="stocks-alloc-name">{s.segment}</div>
                      <div className="stocks-alloc-meta">{toNumber(s.percent).toFixed(1)}%</div>
                    </div>
                    <div className="stocks-alloc-bar">
                      <span style={{ width: `${Math.min(toNumber(s.percent), 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isEditing && (
              <div className="stocks-exposure-edit">
                <div className="stocks-exposure-group">
                  <div className="stocks-exposure-group-title">Geographic Revenue Exposure</div>
                  {editForm.countries.map((c, idx) => (
                    <div className="stocks-exposure-edit-row" key={idx}>
                      <input
                        className="stocks-exposure-input"
                        type="text"
                        placeholder="Country"
                        value={c.country}
                        onChange={(e) => updateRow("countries", idx, "country", e.target.value)}
                      />
                      <input
                        className="stocks-target-input"
                        type="number"
                        placeholder="%"
                        value={c.percent}
                        onChange={(e) => updateRow("countries", idx, "percent", e.target.value)}
                      />
                      <button className="btn btn-secondary" onClick={() => removeRow("countries", idx)}>✕</button>
                    </div>
                  ))}
                  <button className="btn btn-secondary" onClick={() => addRow("countries")}>+ Add Country</button>
                </div>

                <div className="stocks-exposure-group">
                  <div className="stocks-exposure-group-title">Business Segment Exposure</div>
                  {editForm.segments.map((s, idx) => (
                    <div className="stocks-exposure-edit-row" key={idx}>
                      <input
                        className="stocks-exposure-input"
                        type="text"
                        placeholder="Segment"
                        value={s.segment}
                        onChange={(e) => updateRow("segments", idx, "segment", e.target.value)}
                      />
                      <input
                        className="stocks-target-input"
                        type="number"
                        placeholder="%"
                        value={s.percent}
                        onChange={(e) => updateRow("segments", idx, "percent", e.target.value)}
                      />
                      <button className="btn btn-secondary" onClick={() => removeRow("segments", idx)}>✕</button>
                    </div>
                  ))}
                  <button className="btn btn-secondary" onClick={() => addRow("segments")}>+ Add Segment</button>
                </div>

                <div className="stocks-exposure-group">
                  <div className="stocks-exposure-group-title">Summary</div>
                  <textarea
                    className="stocks-exposure-textarea"
                    rows={3}
                    value={editForm.summary}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, summary: e.target.value }))}
                  />
                </div>

                <div className="stocks-exposure-edit-actions">
                  <button className="btn btn-primary" onClick={() => saveEdit(profile)} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button className="btn btn-secondary" onClick={cancelEdit} disabled={saving}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
